import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listNotifications,
  markRead,
  markAllRead,
  type Notification,
} from "@/api/notifications";

const POLL_INTERVAL = 30_000; // 30 s

export default function NotificationBell() {
  const { t } = useTranslation();
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unread = items.filter((n) => !n.is_read).length;

  // Fetch on mount + periodic poll
  useEffect(() => {
    const load = () =>
      listNotifications()
        .then(setItems)
        .catch(() => {});
    load();
    const id = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(id);
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMarkRead = async (id: string) => {
    await markRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const typeIcon: Record<string, string> = {
    info: "ℹ️",
    warning: "⚠️",
    success: "✅",
    error: "❌",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        className="btn-icon"
        onClick={() => setOpen(!open)}
        title="Notifications"
        style={{ position: "relative", fontSize: "1.2rem", background: "none", border: "none", cursor: "pointer" }}
      >
        🔔
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -6,
              background: "var(--color-danger, #e74c3c)",
              color: "#fff",
              borderRadius: "50%",
              fontSize: "0.65rem",
              minWidth: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            marginTop: 8,
            width: 340,
            maxHeight: 420,
            overflowY: "auto",
            background: "var(--bg-card, #fff)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,.15)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              borderBottom: "1px solid var(--border-color, #eee)",
            }}
          >
            <strong>{t("notification.title")}</strong>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ fontSize: "0.78rem", background: "none", border: "none", cursor: "pointer", color: "var(--color-primary, #3498db)" }}
              >
                {t("notification.markAllRead")}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p style={{ padding: 16, textAlign: "center", color: "#999" }}>{t("notification.empty")}</p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                onClick={() => !n.is_read && handleMarkRead(n.id)}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid var(--border-color, #f0f0f0)",
                  background: n.is_read ? "transparent" : "var(--bg-highlight, #f0f7ff)",
                  cursor: n.is_read ? "default" : "pointer",
                }}
              >
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span>{typeIcon[n.notification_type] ?? "ℹ️"}</span>
                  <strong style={{ fontSize: "0.88rem" }}>{n.title}</strong>
                </div>
                {n.message && (
                  <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "#666" }}>{n.message}</p>
                )}
                <time style={{ fontSize: "0.72rem", color: "#aaa" }}>
                  {new Date(n.created_at).toLocaleString("fr")}
                </time>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
